import { useCallback, useEffect, useState } from 'react';
import { useWatch } from 'react-hook-form';
import InputDecorator from '@enerex/unified-ui-react/form/InputDecorator';
import Select from 'components/UnifiedSelect';
import useShowSnackbar from 'hooks/useShowSnackbar';
import { toLookupOption } from '../components/lookupOption';
import { loadBaseUomRecords, matchesBaseUom, isUomGuid, sameUomId } from './baseUomOptions';

const BaseUomField = ({ field, control, setValue, disabled }) => {
    const [commodityId, dimensionId, selectedId, selectedName] = useWatch({ control,
        name: ['commodityId', 'dimensionId', field.name, field.labelField] });
    const ready = isUomGuid(commodityId) && isUomGuid(dimensionId);
    const key = `${commodityId}:${dimensionId}`;
    const [state, setState] = useState({ key: '', records: [], loading: false, error: '' });
    const [portal, setPortal] = useState(null);
    const attach = useCallback(element => setPortal(element?.closest('[role="dialog"]') ?? null), []);
    const snackbar = useShowSnackbar();
    useEffect(() => {
        if (!ready) return;
        const controller = new AbortController();
        let current = true;
        setState({ key, records: [], loading: true, error: '' });
        loadBaseUomRecords(controller.signal).then(records => {
            if (current) setState({ key, records, loading: false, error: '' });
        }).catch(error => {
            if (current) setState({ key, records: [], loading: false, error: error.message });
        });
        return () => { current = false; controller.abort(); };
    }, [key, ready]);
    useEffect(() => { if (state.error) snackbar(state.error, 'error'); }, [state.error, snackbar]);
    const loading = ready && (state.key !== key || state.loading);
    const options = ready && state.key === key ? state.records.filter(record => matchesBaseUom(record, commodityId, dimensionId))
        .map(toLookupOption).sort((a, b) => a.label.localeCompare(b.label)) : [];
    const selected = options.find(option => sameUomId(option.value, selectedId));
    const selectedValue = selected?.value;
    const selectedLabel = selected?.label;
    useEffect(() => {
        if (!ready || loading || state.error || !selectedId) return;
        if (selectedValue == null) {
            setValue(field.name, '', { shouldDirty: true });
            setValue(field.labelField, '', { shouldDirty: true });
        } else if (selectedLabel !== selectedName) setValue(field.labelField, selectedLabel);
    }, [ready, loading, state.error, selectedId, selectedValue, selectedLabel, selectedName, field.name, field.labelField, setValue]);
    return <div ref={attach}>
        <InputDecorator name={field.name} control={control} label={`${field.label} *`}
            rules={{ validate: value => !ready ? 'Select a Commodity and Dimension first.'
                : loading ? 'Wait for base units to load.' : state.error ? 'Base units could not be loaded.'
                    : options.some(option => sameUomId(option.value, value)) || 'Select a matching Base UOM.' }}
            render={({ field: input, fieldState }) => <Select inputId="lookup-baseUoMId" aria-label={field.label}
                className="settings-lookup__form-select" options={options} isSearchable isClearable={false}
                value={selected || (loading && selectedId ? { value: selectedId, label: selectedName || 'Loading base unit...' } : null)}
                isDisabled={disabled || !ready || loading || Boolean(state.error)} isLoading={loading} invalid={fieldState.invalid}
                placeholder={ready ? 'Select base UOM' : 'Select Commodity and Dimension first'}
                menuPortalTarget={portal} menuPosition="fixed" onBlur={input.onBlur}
                onChange={option => { input.onChange(option?.value ?? ''); setValue(field.labelField, option?.label ?? '', { shouldDirty: true }); }}
                noOptionsMessage={() => 'No base units match this Commodity and Dimension.'} />} />
    </div>;
};
export default BaseUomField;
